import { ASSIGNMENT_CREATE_MODES } from 'constants/assignments';
import {
  chain,
  filter,
  forEach,
  get,
  groupBy,
  isEmpty,
  map,
  omit,
  orderBy
} from 'lodash';
import moment from 'moment';

import { flattenObjectives } from 'utils/trackers';

export const splitAssignmentsToMonths = ({ assignments }) => {
  const sortedAssignments = orderBy(assignments, 'due_at', 'desc').map(
    (assignment) => ({
      ...assignment,
      image: get(assignment, 'tasks.[0].work[0].work_url'),
      isEmptyWork: isEmpty(get(assignment, 'tasks.[0].work[0]')),
      month: moment(assignment.due_at).format('MMMM')
    })
  );

  return groupBy(sortedAssignments, 'month');
};

export const buildFormDataForCreate = ({ values }) => {
  const tasksWithStandardIds = map(values.tasks, (task) => ({
    ...task,
    standard_ids: task.standards.map((standard) => standard.id)
  }));

  const tasksWithOmittedStandards = map(tasksWithStandardIds, (task) =>
    omit(task, 'standards')
  ); // We do not need to pass the "standards" key to the BE

  const filteredObjectives = filter(
    flattenObjectives(tasksWithOmittedStandards),
    (objective) => !isEmpty(objective.name)
  );

  // Set `order` for each objective to be the consecutive index, since that's how we want them ordered.
  const objectives = map(filteredObjectives, (objective, index) => ({
    ...objective,
    order: index
  }));

  const formData = new FormData();
  formData.append('name', values.assignmentName);
  formData.append('description', values.description);

  forEach(values.attachments, (attachment) =>
    formData.append('attachment_file_ids', attachment.id)
  );

  forEach(objectives, (obj) =>
    formData.append('objectives', JSON.stringify(obj))
  );

  if (Object.keys(values).includes('isAcceleratedCurriculum')) {
    formData.append('is_accelerated', values.isAcceleratedCurriculum);
  }

  return formData;
};

export const buildStepperFormDataForCreate = ({ values }) => {
  const formData = new FormData();
  formData.append('name', values.assignmentName);
  formData.append('description', values.description || '');

  const standardIds = chain(values.tasks)
    .map('standards')
    .flatten()
    .map('id')
    .uniq()
    .value();

  const numberOfPages = parseInt(values.pageCount) || 1;

  for (let i = 0; i < numberOfPages; i++) {
    const objective = {
      name: `Page ${i + 1}`,
      value_type: values.rubric,
      max_value: values.maxValue,
      depth: 1,
      description: '',
      children: [],
      standard_ids: standardIds,
      isHeader: true,
      order: i,
      disableEdit: false
    };

    formData.append('objectives', JSON.stringify(objective));
  }

  if (values.attachments?.length) {
    values.attachments.forEach((attachment) => {
      if (attachment.id) {
        formData.append('attachment_file_ids', attachment.id);
      }
    });
  }

  if (Object.keys(values).includes('isAcceleratedCurriculum')) {
    formData.append('is_accelerated', values.isAcceleratedCurriculum);
  }

  return formData;
};

export const buildFormDataForUpdate = ({ values }) => {
  const tasksWithStandardIds = map(values.tasks, (task) => ({
    ...task,
    standard_ids: task.standards.map((standard) => standard.id)
  }));

  const tasksWithOmittedStandards = map(tasksWithStandardIds, (task) =>
    omit(task, 'standards')
  ); // We do not need to pass the "standards" key to the BE

  const filteredObjectives = map(tasksWithOmittedStandards, (task) => {
    task.children = filter(
      task.children,
      (criteria) => !isEmpty(criteria.name)
    );
    return task;
  });

  let lastOrder = -1;
  const updatedTasks = map(filteredObjectives, (parent, index) => {
    const order = lastOrder + 1;
    const children = map(parent.children, (child, childIndex) => {
      const childOrder = order + childIndex + 1;
      return { ...child, order: childOrder };
    });

    lastOrder = order + children.length;

    return { ...parent, order, children };
  });

  const formData = new FormData();
  formData.append('name', values.assignmentName);
  formData.append('description', values.description);

  forEach(values.attachments, (attachment) =>
    formData.append('attachment_file_ids', attachment.id)
  );

  forEach(updatedTasks, (obj) =>
    formData.append('objectives', JSON.stringify(obj))
  );

  return formData;
};

export const buildStepperFormDataForUpdate = ({ values }) => {
  const tasksWithStandardIds = map(values.tasks, (task) => ({
    ...task,
    standard_ids: task.standards.map((standard) => standard.id)
  }));
  map(tasksWithStandardIds, (task) => omit(task, 'standards'));
  const numberOfPages = parseInt(values.pageCount) || 1;
  let objectives = [];

  for (let i = 0; i < numberOfPages; i++) {
    let objective;
    if (values.tasks[i]) {
      objective = values.tasks[i];
    } else {
      objective = {
        name: `Page ${i + 1}`,
        value_type: values.rubric || 'rubric_zero_four',
        max_value: values.maxValue,
        depth: 1,
        description: '',
        children: [],
        standards: values.tasks[0]['standards'],
        isHeader: true,
        order: i,
        disableEdit: false
      };
    }

    objectives.push(objective);
  }

  let lastOrder = -1;
  const updatedObjectives = map(objectives, (objective, index) => {
    const order = lastOrder + 1;
    const children = map(objective.children, (child, childIndex) => {
      const childOrder = order + childIndex + 1;
      return { ...child, order: childOrder };
    });

    lastOrder = order + children.length;

    return { ...objective, order, children };
  });

  const formData = new FormData();
  formData.append('name', values.assignmentName);
  formData.append('description', values.description || '');

  forEach(updatedObjectives, (obj) =>
    formData.append('objectives', JSON.stringify(obj))
  );

  if (values.attachments?.length) {
    values.attachments.forEach((attachment) => {
      if (attachment.id) {
        formData.append('attachment_file_ids', attachment.id);
      }
    });
  }

  return formData;
};

export const getRenderedSectionName = ({ selected, userSections }) => {
  if (selected === 'placeholder' || !selected) {
    return 'Assignment Section';
  }

  const selectedSection = userSections.find((sec) => sec.id === selected);
  const name = selectedSection ? selectedSection.name : '';

  return name.length > 18 ? `${name.substring(0, 18)}...` : name;
};

export const getOptionLabel = ({ option }) => {
  const code = get(option, 'code', '');
  const description = get(option, 'short_description', '');
  return code === description ? code : `${code} - ${description}`;
};

export const buildAssignmentFormData = ({
  assignmentName,
  description,
  uploadedFile,
  selectedRubric,
  selectedStandards
}) => {
  const formData = new FormData();
  const standardIds = selectedStandards.map((standard) => standard.id);

  formData.append('name', assignmentName);
  formData.append('description', description);

  const pageCount = uploadedFile?.pages || 0;

  for (let i = 0; i < pageCount; i++) {
    const objectivePayload = {
      name: `Page ${i + 1}`,
      value_type: selectedRubric.key,
      max_value: 100,
      depth: 1,
      description: '',
      children: [],
      standard_ids: standardIds,
      isHeader: true,
      order: i,
      disableEdit: false
    };
    formData.append('objectives', JSON.stringify(objectivePayload));
  }

  if (uploadedFile?.id) {
    formData.append('attachment_file_ids', uploadedFile.id);
  }

  return formData;
};

export const validateAssignmentForm = ({
  mode,
  assignmentName,
  description,
  selectedSections,
  selectedRubric,
  selectedStandards,
  uploadedFile
}) => {
  const isBaseValid =
    assignmentName.trim() !== '' &&
    description.trim() !== '' &&
    selectedSections.length > 0 &&
    selectedRubric !== null;

  const isScratchValid =
    selectedStandards.length > 0 &&
    uploadedFile !== null &&
    uploadedFile.pages !== null;

  return mode === ASSIGNMENT_CREATE_MODES.TEMPLATE
    ? isBaseValid
    : isBaseValid && isScratchValid;
};
